import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Phone, CheckCircle2, ArrowRight, MapPin, Star } from "lucide-react";
import { api } from "../lib/api";
import CTASection from "../components/CTASection";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { renderBody, getRelatedCities } from "../lib/cityLinking";
import { buildCityJsonLd } from "../lib/seoSchema";
import { applyOGTags, clearOGTags } from "../lib/ogTags";

export default function CityPage({ slugOverride }) {
  const params = useParams();
  const slug = slugOverride || params.slug;
  const [city, setCity] = useState(null);
  const [allCities, setAllCities] = useState([]);
  const [services, setServices] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get(`/cities/${slug}`),
      api.get("/cities"),
      api.get("/services"),
      api.get("/reviews"),
    ])
      .then(([c, cs, s, r]) => {
        setCity(c.data);
        setAllCities(cs.data);
        setServices(s.data);
        setReviews(r.data);
        document.title = c.data.meta_title;
        const meta = document.querySelector('meta[name="description"]');
        if (meta) meta.setAttribute("content", c.data.meta_description);
        window.scrollTo(0, 0);
      })
      .catch(() => setCity(null))
      .finally(() => setLoading(false));
  }, [slug]);

  const relatedCities = useMemo(
    () => getRelatedCities(city, allCities),
    [city, allCities]
  );

  // Shared tracker: each related city gets max 1 contextual link in the entire article.
  // Rebuild on city/cities change so navigating between pages resets it.
  const linkedSet = useMemo(() => new Set(), [city?.slug, allCities.length]);

  // Inject JSON-LD structured data (LocalBusiness + AggregateRating + FAQPage + Breadcrumb)
  // and a canonical <link> tag for this city's keyword-rich URL. Cleans up on slug change.
  useEffect(() => {
    if (!city) return;
    const parentCity = city.parent ? allCities.find((c) => c.slug === city.parent) : null;
    const ld = buildCityJsonLd({ city, parentCity, allCities, reviews });

    const scriptId = "city-jsonld";
    document.getElementById(scriptId)?.remove();
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = scriptId;
    script.text = JSON.stringify(ld);
    document.head.appendChild(script);

    const canonicalId = "city-canonical";
    document.getElementById(canonicalId)?.remove();
    const canonical = document.createElement("link");
    canonical.rel = "canonical";
    canonical.id = canonicalId;
    canonical.href = `${window.location.origin}/pool-service-${city.slug}`;
    document.head.appendChild(canonical);

    applyOGTags({
      title: city.meta_title,
      description: city.meta_description,
      image: city.hero_image,
      url: `${window.location.origin}/pool-service-${city.slug}`,
      type: "website",
    });

    return () => {
      document.getElementById(scriptId)?.remove();
      document.getElementById(canonicalId)?.remove();
      clearOGTags();
    };
  }, [city, allCities, reviews]);

  if (loading) {
    return (
      <div className="section-container py-20 text-center" data-testid="city-loading">
        Loading...
      </div>
    );
  }

  if (!city) {
    return (
      <div className="section-container py-20 text-center">
        <h1 className="text-3xl font-bold mb-4">Location not found</h1>
        <Link to="/locations" className="btn-primary">View all service areas</Link>
      </div>
    );
  }

  const content = city.content;
  const parentCity = city.parent ? allCities.find((c) => c.slug === city.parent) : null;

  return (
    <div data-testid={`city-page-${city.slug}`}>
      {/* HERO */}
      <section className="relative overflow-hidden bg-brand-navy">
        <div className="absolute inset-0">
          <img
            src={city.hero_image}
            alt={`${city.name} pool service`}
            className="w-full h-full object-cover opacity-60"
          />
          {/* Left-to-right wash so headline reads while local landmark stays recognizable on the right */}
          <div className="absolute inset-0 bg-gradient-to-r from-brand-navy via-brand-navy/85 to-brand-navy/30" />
          <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/80 via-transparent to-transparent" />
        </div>
        <div className="relative section-container py-20 lg:py-28">
          <nav className="flex items-center gap-2 text-sm text-brand-pink font-semibold mb-6" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span className="text-slate-400">/</span>
            <Link to="/locations" className="hover:text-white transition-colors">Service Areas</Link>
            {parentCity && (
              <>
                <span className="text-slate-400">/</span>
                <Link to={`/pool-service-${parentCity.slug}`} className="hover:text-white transition-colors" data-testid="breadcrumb-parent">
                  {parentCity.name}
                </Link>
              </>
            )}
            <span className="text-slate-400">/</span>
            <span className="text-white">{city.name}</span>
          </nav>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-6 max-w-4xl leading-tight">
            {content.hero_title}
          </h1>
          <p className="text-lg lg:text-xl text-slate-200 max-w-3xl mb-8 leading-relaxed">
            {content.hero_subtitle}
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link to="/contact" className="btn-primary" data-testid="city-cta-estimate">
              Get Free Estimate <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href="tel:7722890405"
              className="inline-flex items-center justify-center gap-2 bg-white/10 backdrop-blur text-white border-2 border-white/20 hover:bg-white hover:text-brand-navy font-bold rounded-lg px-6 py-3 transition-all"
              data-testid="city-cta-phone"
            >
              <Phone className="w-4 h-4" /> 772-289-0405
            </a>
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section className="py-16 lg:py-24">
        <div className="section-container grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2 prose-content">
            <p className="text-xl text-slate-700 leading-relaxed font-medium">
              {renderBody(content.intro, allCities, city.slug, linkedSet)}
            </p>
            {parentCity && (
              <p className="mt-4 text-slate-600">
                {city.name} is part of the greater{" "}
                <Link
                  to={`/pool-service-${parentCity.slug}`}
                  className="text-brand-blue font-semibold hover:text-brand-pink underline underline-offset-2"
                  data-testid="intro-parent-link"
                >
                  {parentCity.name} pool service area
                </Link>
                . For full regional coverage details, visit our main {parentCity.name} page.
              </p>
            )}
          </div>
          <div className="bg-gradient-to-br from-brand-pink to-brand-pink-hover rounded-2xl text-white shadow-xl shadow-pink-500/20 h-fit overflow-hidden">
            <div className="relative h-48 w-full overflow-hidden">
              <img
                src={city.hero_image}
                alt={`Pool service in ${city.name}, FL`}
                className="w-full h-full object-cover"
                loading="lazy"
                data-testid={`city-showcase-image-${city.slug}`}
              />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-brand-pink/90 to-transparent p-3">
                <div className="text-white font-bold text-sm flex items-center gap-1.5">
                  <MapPin className="w-3.5 h-3.5" /> {city.name}, FL
                </div>
              </div>
            </div>
            <div className="p-7">
              <div className="overline text-white/90 mb-3">Quick Contact</div>
              <div className="font-heading font-extrabold text-3xl mb-2">772-289-0405</div>
              <p className="text-white/90 text-sm mb-6">
                Call today for a free quote on pool service in {city.name}.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-white text-brand-pink font-bold rounded-lg px-5 py-2.5 hover:bg-slate-100 transition-colors"
                data-testid="city-sidebar-cta"
              >
                Request Estimate <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CONTENT SECTIONS */}
      <section className="pb-16 lg:pb-24">
        <div className="section-container grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2 space-y-12">
            {content.sections.map((section, idx) => (
              <div key={idx} className="prose-content">
                <h2 className="text-2xl sm:text-3xl font-extrabold mb-4 text-brand-navy">
                  {section.heading}
                </h2>
                <div className="text-slate-700 leading-relaxed">
                  {renderBody(section.body, allCities, city.slug, linkedSet)}
                </div>
              </div>
            ))}

            {/* FAQs */}
            {content.faqs && content.faqs.length > 0 && (
              <div className="pt-8 border-t border-slate-200">
                <h2 className="text-2xl sm:text-3xl font-extrabold mb-6">
                  Frequently Asked Questions
                </h2>
                <Accordion type="single" collapsible className="w-full">
                  {content.faqs.map((faq, i) => (
                    <AccordionItem key={i} value={`item-${i}`} data-testid={`city-faq-${i}`}>
                      <AccordionTrigger className="text-left font-bold text-brand-navy">
                        {faq.q}
                      </AccordionTrigger>
                      <AccordionContent className="text-slate-700">{faq.a}</AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            )}
          </div>

          {/* SIDEBAR */}
          <aside className="space-y-6 lg:sticky lg:top-28 lg:self-start">
            {/* Services panel: only show on parent (major-city) pages — adds noise on supporting-geo sub-pages */}
            {!city.parent && (
            <div className="bg-white rounded-2xl border border-slate-200 p-7 shadow-sm">
              <div className="overline mb-3">Our Services in {city.name}</div>
              <div className="space-y-2">
                {services.map((s) => (
                  <Link
                    key={s.slug}
                    to={`/services/${s.slug}`}
                    className="flex items-start gap-3 p-3 rounded-lg hover:bg-slate-50 group"
                    data-testid={`city-service-${s.slug}`}
                  >
                    <CheckCircle2 className="w-4 h-4 text-brand-pink mt-1 flex-shrink-0" />
                    <div>
                      <div className="font-semibold text-sm text-brand-navy group-hover:text-brand-pink">
                        {s.name}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
            )}

            {relatedCities.length > 0 && (
              <div className="bg-slate-50 rounded-2xl border border-slate-200 p-7">
                <div className="overline mb-3">Nearby Service Areas</div>
                <ul className="space-y-2">
                  {relatedCities.map((rc) => (
                    <li key={rc.slug}>
                      <Link
                        to={`/pool-service-${rc.slug}`}
                        className="text-sm font-semibold text-slate-700 hover:text-brand-pink flex items-center justify-between group"
                        data-testid={`nearby-${rc.slug}`}
                      >
                        <span>Pool Service in {rc.name}</span>
                        <ArrowRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="bg-brand-navy rounded-2xl p-7 text-white">
              <Star className="w-8 h-8 text-amber-400 mb-3 fill-amber-400" />
              <div className="text-xl font-heading font-extrabold mb-2">
                20+ Years of Treasure Coast Experience
              </div>
              <p className="text-sm text-slate-300 mb-4">
                Family-owned and trusted by homeowners across the region.
              </p>
              <Link to="/reviews" className="text-brand-pink font-semibold text-sm hover:text-white flex items-center gap-1">
                Read reviews <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </aside>
        </div>
      </section>

      <CTASection locationName={city.name} />
    </div>
  );
}
